<template>
  <div>
    <card-wrapper :title="$t('import.title')" id="import-export-files">
      <div class="ml-lg-6 d-flex justify-lg-space-between align-center">
        <div class="section-title">
          {{ $t('import.title-global') }}
        </div>
      </div>

      <div class="title px-lg-16 pb-3 px-0">
        {{ $t('import.title-predeliveries') }}
      </div>
      <div class="px-lg-16 p-0">
        <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0 pb-6">
          <v-col cols="12" lg="8">
            <div class="subtitle d-flex flex-row align-lg-center align-start pt-0">
              <i class="fa-light fa-upload"></i>
              {{ $t('import.pre-deliveries.label') }}
            </div>
            <div class="description pb-lg-3 pb-6">{{ $t('import.pre-deliveries.sub-label') }}</div>
          </v-col>
          <v-col cols="12" lg="4">
            <div class="d-flex justify-lg-end justify-center align-center full-height">
              <custom-input-file
                data-cy="pre-deliveries-input"
                :key="`import_pre_deliveries_${refreshKey}`"
                type="s3"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                @importAction="importPredeliveriesFile"
                :loading="importPredeliveryLoading" />
            </div>
          </v-col>
        </div>
      </div>

      <v-divider class="mx-lg-6" />

      <div class="ml-lg-6 d-flex justify-lg-space-between align-center">
        <div class="section-title">
          {{ $t('import.title-by-branch') }}
        </div>

        <div class="branch-selector">
          <span class="pr-6">{{ $t('import.models-import.branch-selector.label') }}</span>
          <m-select
            v-model="selectedBranch"
            item-text="label"
            item-value="value"
            :items="branches"
            :multiple="false"
            class="m-select__branch"
            data-cy="current-branch">
            <template v-slot:prepend-inner>
              <div class="d-flex justify-center align-items-center m-select__branch__flag">
                <country-flag :country="selectedBranch" />
              </div>
            </template>
          </m-select>
        </div>
      </div>

      <div class="title pl-lg-16 pb-3 px-0 pt-0">
        {{ $t('import.title-models') }}
      </div>
      <div class="px-lg-16 p-0 pt-0">
        <v-divider />
        <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0">
          <v-col cols="12" lg="8">
            <div class="subtitle d-flex flex-row align-lg-center align-start">
              <i class="fa-light fa-upload"></i>
              {{ $t('import.models-import.label') }}
            </div>
            <div class="description pb-lg-3 pb-6">
              {{ $t('import.models-import.sub-label') }}
            </div>
          </v-col>
          <v-col cols="12" lg="4">
            <div class="d-flex justify-lg-end justify-center align-center full-height">
              <custom-input-file
                :key="`import_models_${refreshKey}`"
                type="s3"
                @importAction="importModelsAndInsurancesFile"
                :loading="importModelsLoading" />
            </div>
          </v-col>
        </div>
        <v-divider />
        <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0 pb-6">
          <v-col cols="12" lg="8">
            <div class="subtitle d-flex flex-row align-lg-center align-start">
              <i class="fa-light fa-arrow-down-to-bracket"></i>
              {{ $t('import.models-download.label') }}
            </div>
            <div class="description pb-lg-3 pb-6">
              {{ $t('import.models-download.sub-label') }}
            </div>
          </v-col>
          <v-col cols="12" lg="4">
            <div class="d-flex justify-lg-end justify-center align-center full-height">
              <custom-input-file
                :key="`download_${refreshKey}`"
                type="button"
                @btnAction="downloadModelsAndInsurancesFile"
                :loading="downloadLoading"
                :label="$t('import.download-file')" />
            </div>
          </v-col>
        </div>
        <v-divider />
      </div>

      <div class="title px-lg-16 pt-6 pb-4 px-0">
        {{ $t('import.title-offers') }}
      </div>

      <div class="px-lg-16 p-0">
        <v-divider class="mt-1" />
        <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0 pb-6">
          <v-col cols="12" lg="5">
            <div class="subtitle d-flex flex-row align-lg-center align-start">
              <i class="fa-light fa-file-export"></i>
              {{ $t('import.offers-data.label') }}
            </div>
            <div class="description pb-lg-3 pb-6">
              {{ $t('import.offers-data.sub-label') }}
            </div>
          </v-col>
          <v-col cols="12" lg="7">
            <div class="d-flex align-center justify-end full-height">
              <v-row no-gutters>
                <v-col cols="12" lg="7">
                  <div class="d-flex align-center justify-center justify-lg-end full-height">
                    <range-picker
                      style="min-width: 250px"
                      :maxDate="maxDate"
                      :minDate="'2021-09-01'"
                      @changeValue="setSelectedRange"
                      :placeholder="$t('import.range-picker-placeholder')"
                      :maxRange="3"
                      :unit="'months'"
                      :initialRange="selectedRange"
                      :locale="locale" />
                  </div>
                </v-col>
                <v-col cols="12" lg="5">
                  <div class="d-flex align-center justify-center justify-lg-end full-height">
                    <custom-input-file
                      :key="`export_${refreshKey}`"
                      type="button"
                      @btnAction="exportOffersData"
                      :loading="exportLoading"
                      :label="$t('import.export-file')" />
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </div>
        <v-divider />
      </div>

      <div class="title px-lg-16 pt-6 pb-3 px-0">
        {{ $t('import.title-registrations') }}
      </div>

      <div class="px-lg-16 p-0">
        <v-divider />
        <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0 pb-6">
          <v-col cols="12" lg="8">
            <div class="subtitle d-flex flex-row align-lg-center align-start">
              <i class="fa-light fa-upload"></i>
              {{ $t('import.vin-data.label') }}
            </div>
            <div class="description pb-lg-3 pb-6">{{ $t('import.vin-data.sub-label') }}</div>
          </v-col>
          <v-col cols="12" lg="4">
            <div class="d-flex justify-lg-end justify-center align-center full-height">
              <custom-input-file
                :key="`import_vin_${refreshKey}`"
                type="s3"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                @importAction="importNewVinFile"
                :loading="importVinLoading" />
            </div>
          </v-col>
        </div>
        <v-divider />
      </div>

      <ImportTargets :selected-branch="selectedBranch" />
    </card-wrapper>
    <dialog-exit-process
      :dialog="cancelDialog"
      @exit="exit"
      @openClose="openCloseCancelDialog"
      :title="$t('offer-manager.exit-popup.title')"
      :description="$t('offer-manager.exit-popup.title')" />
    <mapit-dialog :dialog="importDialog.open" @close="importDialog.close()">
      <template v-slot:header-icon>
        <v-icon class="text-orange" v-if="importDialog.error"> fa fa-exclamation-triangle </v-icon>
        <v-icon class="text-gray" v-else-if="importDialog.info"> fa-solid fa-circle-info </v-icon>
        <v-icon class="text-secondary" v-else>fa fa-check</v-icon>
      </template>
      <template v-slot:header-text>
        <div class="text-center">{{ importDialog.title }}</div>
      </template>
      <template v-slot:body>
        <div v-html="importDialog.body" class="text-center"></div>
      </template>
      <template v-slot:footer-center>
        <v-btn class="btn-gray" v-if="importDialog.showCancel" @click="importDialog.close()"
          >{{ $t('buttons.cancel') }}
        </v-btn>
        <v-btn class="btn-primary" @click="importDialog.continue()">{{ $t('buttons.next') }} </v-btn>
      </template>
    </mapit-dialog>
  </div>
</template>

<script lang="ts">
// @ts-ignore
import {Country} from '@mapit/common-utils/dist/types'
import {computed, defineComponent, onMounted, onUnmounted, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'
import XLSX from 'xlsx'

import {hondaCatalogApi, Import} from '@/api/honda-catalog.api'
// @ts-ignore
import {s3, streamToString} from '@/api/lib/s3-client'
// @ts-ignore
import apiOffers from '@/api/offers.api'
// @ts-ignore
import countries from '@/assets/json/country.json'
import DialogExitProcess from '@/components/dialog/DialogExitProcess.vue'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import CardWrapper from '@/components/general/CardWrapper.vue'
import RangePicker from '@/components/general/RangePicker.vue'
import CustomInputFile from '@/components/import/CustomInputFile.vue'
import ImportTargets from '@/components/import/ImportTargets.vue'
// @ts-ignore
import {downloadFiles} from '@/helpers/downloadFile.js'
import {validateXlsxFile} from '@/helpers/import'
import {PredeliveryResponse} from '@/types'
import {Branch} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

const {VUE_APP_MAPIT_CATALOG_IMPORTS_S3, VUE_APP_S3_OFFER_DATA_BUCKET} = process.env

/**
 * The custom HTML `<data-import />` component.
 * /import This is a separate page with AWS authentication role security.
 *
 * @author Julià Soler
 * @license Mapit IoT S.L.
 */
export default defineComponent({
  name: 'DataImport',
  components: {RangePicker, CustomInputFile, DialogExitProcess, MapitDialog, ImportTargets, CardWrapper},
  setup(props, {root}) {
    const selectedRange = ref([
      DateTime.now().minus({months: 3}).plus({days: 1}).toFormat('yyyy-MM-dd'),
      DateTime.now().toFormat('yyyy-MM-dd'),
    ])
    const interval = ref<any>(null)

    const importDialog = ref<
      Partial<{
        open: boolean
        title: string
        body: string
        error: boolean
        info: boolean
        showCancel: boolean
        click: () => void
        continue: () => void
        close: () => void
      }>
    >({
      open: false,
      error: false,
      info: false,
      showCancel: false,
      title: '',
      body: '',
      click: () => closeImportDialog(),
      continue: () => ({}),
      close: () => closeImportDialog(),
    })

    const cancelDialog = ref(false)
    const downloadLoading = ref(false)
    const exportLoading = ref(false)
    const importModelsLoading = ref(false)
    const importPredeliveryLoading = ref(false)
    const importVinLoading = ref(false)
    const refreshKey = ref(0)
    const selectedBranch = ref(Branch.HondaSpain)

    const {getLocale: locale, getGroup: group, isImportUser, isAdmin, getUser} = mapGetters(root.$store.getters)

    const allowImport = computed(() => {
      return isImportUser.value || isAdmin.value
    })

    const branches = computed(() => {
      return Object.values(Branch)
        .filter(branch => branch !== Branch.HondaUSA)
        .map(branch => ({
          value: branch,
          label: root.$t(`branch.label.${branch}`),
        }))
    })

    const maxDate = computed(() => DateTime.now().toFormat('yyyy-MM-dd'))

    watch(selectedBranch, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        await root.$store.dispatch('setAlertResult', {
          type: 'success',
          stringId: 'import.models-import.branch-selector.snackbar',
          open: true,
        })
      }
    })

    onMounted(() => {
      if (!allowImport.value) {
        root.$router.push('/')
      }
    })

    onUnmounted(() => {
      clearInterval(interval.value)
      interval.value = null
    })

    function setSelectedRange(range: any) {
      selectedRange.value = range
    }

    /**
     * Import the XLSX file with the new "VIN" list into BE
     * @param {Object} data - Parsed data received from file-input
     */
    async function importNewVinFile(data: any) {
      importVinLoading.value = true
      try {
        const arrayData = await data.arrayBuffer()
        // Validate that the file is a valid XLSX file
        if (!validateXlsxFile(arrayData)) {
          throw new Error(root.$t('import.error-import-format') as string)
        }

        const checkKey = `vins/branch=${selectedBranch.value}/${DateTime.now().toSeconds().toFixed()}`
        const key = `${checkKey}/vins.xlsx`

        // Convert to Uint8Array for AWS SDK v3 compatibility
        const uint8Array = new Uint8Array(arrayData)

        const client = await s3()
        await client.upload({
          bucket: VUE_APP_MAPIT_CATALOG_IMPORTS_S3!,
          key,
          body: uint8Array,
          userName: root.$store.state.fullUserName,
          email: getUser.value.login,
        })

        interval.value = setInterval(checkVinsUploadStatus, 5000, checkKey)
      } catch (err: any) {
        const key = err.code === 'CustomError' ? err.errorKey : 'import.error-import-server'
        importVinLoading.value = false
        importDialog.value = {
          open: true,
          error: true,
          title: root.$t('import.import-title') as string,
          body: root.$t(key) as string,
          continue: () => {
            importVinLoading.value = false
            closeImportDialog()
          },
          close: () => {
            importVinLoading.value = false
            closeImportDialog()
          },
        }
      }
    }

    /**
     * Import the Excell file with the new models and insurances list into BE
     * @param {Object} data - Parsed data recived from file-input
     */
    async function importModelsAndInsurancesFile(data: any) {
      importModelsLoading.value = true
      try {
        const arrayData = await data.arrayBuffer()
        const workbook = XLSX.read(arrayData, {type: 'array'})
        if (!workbook.Sheets['config']) throw {code: 'CustomError', errorKey: 'import.error-import-config-missing'}
        const config = XLSX.utils.sheet_to_json(workbook.Sheets['config'])
        const cr = config[0] as any
        if (
          ![Country.Spain, Country.Portugal].includes(cr.branch) ||
          cr.branch !== selectedBranch.value ||
          !parseInt(cr.vat)
        )
          throw {code: 'CustomError', errorKey: 'import.error-import-config-error'}

        importDialog.value = {
          open: true,
          error: false,
          info: true,
          showCancel: true,
          title: root.$t('import.import-title') as string,
          body: root.$t(
            'import.import-by-country-confirmation',
            countries.find((c: any) => c.code === cr.branch),
          ) as string,
          continue: async () => {
            const checkKey = `models/HDP_${DateTime.now().toSeconds().toFixed()}`
            const key = `${checkKey}.xlsx`

            // Convert to Uint8Array for AWS SDK v3 compatibility
            const uint8Array = new Uint8Array(arrayData)

            const client = await s3()
            await client.upload({
              bucket: VUE_APP_MAPIT_CATALOG_IMPORTS_S3!,
              key,
              body: uint8Array,
              userName: root.$store.state.fullUserName,
              email: getUser.value.login,
            })

            interval.value = setInterval(checkUploadStatus, 5000, checkKey)
            importModelsLoading.value = true
            closeImportDialog()
          },
          close: () => {
            importModelsLoading.value = false
            closeImportDialog()
          },
        }
      } catch (err: any) {
        const key = err.code === 'CustomError' ? err.errorKey : 'import.error-import-server'
        importModelsLoading.value = false
        importDialog.value = {
          open: true,
          error: true,
          title: root.$t('import.import-title') as string,
          body: root.$t(key) as string,
          continue: () => {
            importModelsLoading.value = false
            closeImportDialog()
          },
          close: () => {
            importModelsLoading.value = false
            closeImportDialog()
          },
        }
      }
    }

    function closeImportDialog() {
      importDialog.value.open = false
      refreshKey.value++
    }

    /**
     * Download excel file with the last list of models and insurances
     */
    function downloadModelsAndInsurancesFile() {
      downloadLoading.value = true
      hondaCatalogApi
        .getLastImport(selectedBranch.value)
        .then(({createdAt, signedUrl}: Import) => {
          let docs = [
            {
              url: signedUrl,
              name: `last_import_${createdAt}.xlsx`,
            },
          ]
          downloadFiles(docs)
        })
        .finally(() => {
          downloadLoading.value = false
          refreshKey.value++
        })
    }

    /**
     * Generate and download updated report of offers done
     * If the user has only selected one date, then the generated report
     * will be of the same day.
     */
    function exportOffersData() {
      exportLoading.value = true
      apiOffers
        .exportOffersList({
          branch: selectedBranch.value,
          from: selectedRange.value[0],
          to: selectedRange.value.length === 2 ? selectedRange.value[1] : selectedRange.value[0],
        })
        .then(res => {
          let docs = [
            {
              url: res.data.downloadUrl,
              name: `export_${DateTime.now().toSeconds().toFixed()}.xlsx`,
            },
          ]
          downloadFiles(docs)
        })
        .finally(() => {
          exportLoading.value = false
          refreshKey.value++
        })
    }

    async function checkGenericUploadStatus(
      key: string,
      bucket: string,
      errorFileName: string,
      successFileName: string,
      loadingRef: {value: boolean},
    ) {
      const client = await s3()
      const promiseError = client.getObject({
        Bucket: bucket,
        Key: `${key}${errorFileName}`,
      })

      const promiseSuccess = client.getObject({
        Bucket: bucket,
        Key: `${key}${successFileName}`,
      })

      return Promise.allSettled([promiseError, promiseSuccess]).then(async ([error, success]: any) => {
        if (error.value?.Body || success.value?.Body) {
          clearInterval(interval.value)
          loadingRef.value = false

          if (error.value?.Body) {
            const errorBody = await streamToString(error.value.Body)
            return (importDialog.value = {
              open: true,
              error: true,
              title: root.$t('import.import-title') as string,
              body: `${root.$t('import.error-import-format')}${errorBody}`,
              continue: () => {
                loadingRef.value = false
                closeImportDialog()
              },
              close: () => {
                loadingRef.value = false
                closeImportDialog()
              },
            })
          } else {
            importDialog.value = {
              open: true,
              error: false,
              title: root.$t('import.import-title') as string,
              body: root.$t('import.success-import-text') as string,
              continue: () => {
                loadingRef.value = false
                closeImportDialog()
              },
              close: () => {
                loadingRef.value = false
                closeImportDialog()
              },
            }
          }
        }
      })
    }

    async function checkUploadStatus(key: string) {
      return checkGenericUploadStatus(
        key,
        VUE_APP_MAPIT_CATALOG_IMPORTS_S3!,
        '.ERROR.txt',
        '.SUCCESS.json',
        importModelsLoading,
      )
    }

    async function checkPredeliveriesUploadStatus(key: string) {
      const client = await s3()
      const result = await client.getObject({
        Bucket: VUE_APP_S3_OFFER_DATA_BUCKET!,
        Key: `${key}.RESULT.json`,
      })

      if (result.Body) {
        clearInterval(interval.value)
        importPredeliveryLoading.value = false

        const bodyString = await streamToString(result.Body)
        const body = JSON.parse(bodyString)
        const isError = Object.hasOwn(body, 'error')

        if (isError) {
          const {error} = body

          let errorMessage = `${root.$t(`import.error-import-server`)}`
          if (error.type === 'WrongImportInput') {
            errorMessage = error.row
              ? `${root.$t(`import.error-import-wrong-column-predeliveries`, {
                  row: error.row + 1,
                  column: error.column,
                })}`
              : `${root.$t(`import.error-import-missing-column-predeliveries`, {
                  column: error.column,
                })}`
          } else if (error.type === 'DuplicatedOfferCode') {
            errorMessage = `${root.$t(`import.error-duplicated-code-predeliveries`, {
              row: error.row + 1,
              dealerCode: error.dealerCode,
              offerCode: error.offerCode,
            })}`
          }

          return (importDialog.value = {
            open: true,
            error: true,
            title: root.$t('import.import-title') as string,
            body: errorMessage,
            continue: () => {
              importPredeliveryLoading.value = false
              closeImportDialog()
            },
            close: () => {
              importPredeliveryLoading.value = false
              closeImportDialog()
            },
          })
        } else {
          const {success} = body as PredeliveryResponse

          importDialog.value = {
            open: true,
            error: false,
            title: root.$t('import.import-title') as string,
            body: root.$t('import.success-import-text-predeliveries', {
              total: success.total,
              updated: success.updated,
              created: success.created,
            }) as string,
            continue: () => {
              importPredeliveryLoading.value = false
              closeImportDialog()
            },
            close: () => {
              importPredeliveryLoading.value = false
              closeImportDialog()
            },
          }
        }
      }
    }

    async function checkVinsUploadStatus(key: string) {
      return checkGenericUploadStatus(
        key,
        VUE_APP_MAPIT_CATALOG_IMPORTS_S3!,
        '/vins.ERROR.txt',
        '/vins.json',
        importVinLoading,
      )
    }

    /**
     * Used to open/close main cancel dialog
     */
    function openCloseCancelDialog() {
      cancelDialog.value = !cancelDialog.value
    }

    /**
     * Used to redirect to home page on use cancel dialog
     */
    function exit() {
      openCloseCancelDialog()
      root.$router.push('/')
    }

    async function importPredeliveriesFile(data: any) {
      importPredeliveryLoading.value = true
      try {
        importDialog.value = {
          open: true,
          error: false,
          showCancel: true,
          title: root.$t('import.import-title') as string,
          body: root.$t(
            'import.import-by-country-confirmation-predelivery',
            countries.find((c: any) => c.code === selectedBranch.value),
          ) as string,
          continue: async () => {
            closeImportDialog()
            importPredeliveryLoading.value = true

            const checkKey = `predeliveries/global/PS_${DateTime.now().toSeconds().toFixed()}`
            const key = `${checkKey}.xlsx`

            // Convert File to Uint8Array for AWS SDK v3 compatibility
            const arrayBuffer = await data.arrayBuffer()
            const uint8Array = new Uint8Array(arrayBuffer)

            const client = await s3()
            await client.upload({
              bucket: VUE_APP_S3_OFFER_DATA_BUCKET!,
              key,
              body: uint8Array,
              userName: root.$store.state.fullUserName,
              email: getUser.value.login,
            })
            interval.value = setInterval(checkPredeliveriesUploadStatus, 5000, checkKey)
          },
          close: () => {
            importPredeliveryLoading.value = false
            closeImportDialog()
          },
        }
      } catch (err: any) {
        const key = err.code === 'CustomError' ? err.errorKey : 'import.error-import-server'
        importPredeliveryLoading.value = false
        importDialog.value = {
          open: true,
          error: true,
          title: root.$t('import.import-title') as string,
          body: root.$t(key) as string,
          continue: () => {
            importPredeliveryLoading.value = false
            closeImportDialog()
          },
          close: () => {
            importPredeliveryLoading.value = false
            closeImportDialog()
          },
        }
      }
    }

    return {
      DateTime,
      maxDate,
      selectedRange,
      interval,
      importDialog,
      cancelDialog,
      downloadLoading,
      exportLoading,
      importPredeliveryLoading: importPredeliveryLoading,
      importModelsLoading,
      importVinLoading,
      refreshKey,
      selectedBranch,
      locale,
      group,
      isImportUser,
      isAdmin,
      allowImport,
      branches,
      setSelectedRange,
      importNewVinFile,
      importModelsAndInsurancesFile,
      closeImportDialog,
      downloadModelsAndInsurancesFile,
      exportOffersData,
      checkGenericUploadStatus,
      checkUploadStatus,
      checkVinsUploadStatus,
      openCloseCancelDialog,
      exit,
      importPredeliveriesFile,
    }
  },
})
</script>

<style lang="scss">
#import-export-files {
  .text-gray {
    color: #bdbdbd !important;
  }

  .section-title {
    text-align: left;
    font: normal normal 600 30px 'ProximaNovaExCnSbold' !important;
    letter-spacing: 0;
    color: var(--v-primary-base);
    text-transform: uppercase;
    opacity: 1;
  }

  .title {
    text-align: left;
    font: normal normal 600 24px/30px 'ProximaNovaExCnSbold' !important;
    letter-spacing: 0;
    color: var(--v-primary-base);
    text-transform: uppercase;
    opacity: 1;
  }

  .subtitle {
    text-align: left;
    font: normal normal normal 16px/24px 'ProximaNovaSemibold' !important;
    letter-spacing: 0;
    color: var(--v-gray-darken4) !important;
    padding: 20px 0 5px 0;
    margin: 0;

    > i {
      font-size: 20px;
      color: black;
      padding: 0 17px;
      width: 54px;
    }
  }

  .description {
    text-align: left;
    font: normal normal normal 14px/24px 'ProximaNovaRegular' !important;
    letter-spacing: 0;
    color: var(--v-gray-ligthen4) !important;
    padding: 5px 0 12px 0;
    margin-left: 54px;
  }

  .my-spacer {
    -webkit-box-flex: 1 !important;
    -ms-flex-positive: 1 !important;
    flex-grow: 1 !important;
  }

  .branch-selector {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 24px;
    margin-bottom: 24px;

    > span {
      font: normal normal 400 15px/33px 'ProximaNovaRegular' !important;
      margin-left: auto;
    }

    .m-input {
      font: normal normal 600 14px/30px 'ProximaNovaRegular' !important;
      max-width: 230px;
      margin-right: 60px;
    }
  }

  .m-select__branch {
    .v-input__prepend-inner {
      min-height: 38px;
      margin: 1px 0 1px 0 !important;
    }

    .m-select__branch__flag {
      padding-right: 8px;
    }
  }

  .branch-snackbar {
    top: 70px;

    .v-snack__wrapper {
      width: 100%;
      height: 60px;
      max-width: 100%;

      border-radius: 0;
      background: #dbefdc;
      margin: 0;
      padding: 0;

      &:before {
        opacity: 0;
      }
    }
  }

  .branch-snackbar--content {
    display: flex;
    justify-content: center;
    color: #4caf50;
    font: normal normal 600 15px/20px 'ProximaNovaRegular';
    width: 100%;
    max-width: 100%;
  }

  .snackbar-text {
    display: flex;
    justify-content: center;
    align-items: center;
    font: normal normal 600 15px/20px 'ProximaNovaRegular';
  }

  .tick-icon {
    position: relative;
    width: 20px;
    height: 20px;
    background: #4caf50;
    border-radius: 50%;
  }

  .tick-icon:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    width: 12px;
    height: 8px;
    border-bottom: 2px solid #dbefdc;
    border-left: 2px solid #dbefdc;
  }
}
</style>
